import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { Dropdown, MenuProps, Radio, RadioChangeEvent } from "antd";
import BasicButton from "@components/core/buttons/BasicButton";
import ChevronDownIcon from "@static/core/icons/icon-chevron-down.svg?react";
import { useTranslation } from "react-i18next";
import { Filters } from "@type/core/dashboard/dashboard.types";
import DateFormat from "@utils/DateFormat";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { DATES_FILTER } from "@utils/Constant";

interface DateFilterProps {
  selectedFilters: Filters;
  setSelectedFilters: Dispatch<SetStateAction<Filters>>;
  internSelectedFilters: Filters;
  setInternSelectedFilters: Dispatch<SetStateAction<Filters>>;
  className?: string;
}

const DateFilter: FunctionComponent<DateFilterProps> = ({
  selectedFilters,
  setSelectedFilters,
  internSelectedFilters,
  setInternSelectedFilters,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState<boolean>(false);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div className="bg-white">
          <div onClick={(e) => e.stopPropagation()} className="pt-8">
            <div className="filter-date-radio-buttons">
              <Radio.Group
                value={internSelectedFilters.selectedDateButton}
                onChange={(e: RadioChangeEvent) => {
                  setInternSelectedFilters({
                    ...internSelectedFilters,
                    selectedDateButton: e.target.value,
                  });
                }}
              >
                {Object.values(DATES_FILTER).map((option, index) => (
                  <Radio.Button key={index} value={option}>
                    {t(`home.dashboard.filters.selectedDateButton.${option}`)}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
            <Calendar
              locale="fr"
              minDetail="decade"
              defaultActiveStartDate={new Date()}
              selectRange
              value={
                internSelectedFilters.datePeriod &&
                internSelectedFilters.datePeriod.beginPeriod
                  ? [
                      new Date(internSelectedFilters.datePeriod.beginPeriod),
                      new Date(internSelectedFilters.datePeriod.endPeriod),
                    ]
                  : null
              }
              onChange={(dates) => {
                dates &&
                  Array.isArray(dates) &&
                  setInternSelectedFilters({
                    ...internSelectedFilters,
                    datePeriod: {
                      beginPeriod: DateFormat.calendarDashboardPeriodDate(
                        dates[0] as Date,
                      ),
                      endPeriod: DateFormat.calendarDashboardPeriodDate(
                        dates[1] as Date,
                      ),
                    },
                  });
              }}
            />
          </div>
          <div className="d-flex align-items-center justify-content-end gap-8 flex-wrap px-16 px-sm-32 pb-8">
            <div className="flex-grow-1">
              <BasicButton
                variant="transparent"
                text={t("home.dashboard.filters.clear")}
                disabled={
                  !(
                    internSelectedFilters.datePeriod &&
                    internSelectedFilters.datePeriod.beginPeriod
                  )
                }
                onClick={() => {
                  setSelectedFilters({
                    ...internSelectedFilters,
                    datePeriod: undefined,
                  });
                }}
              />
            </div>
            <BasicButton
              variant="transparent"
              text={t("home.dashboard.filters.validate")}
              disabled={
                !(
                  internSelectedFilters.datePeriod &&
                  internSelectedFilters.datePeriod.beginPeriod
                )
              }
              onClick={() => setSelectedFilters(internSelectedFilters)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={`app-filter-date ${className}`}>
      <Dropdown
        overlayClassName="dropdown-filter-overlay"
        menu={{ items }}
        trigger={["click"]}
        onOpenChange={(open) => setIsDateDropdownOpen(open)}
        placement="bottomRight"
      >
        <BasicButton
          className={`filters-dropdown-button date-dropdown-button ${selectedFilters.datePeriod ? "with-dates" : ""}`}
          variant={
            isDateDropdownOpen || selectedFilters.datePeriod
              ? "primary"
              : "primary-outlined"
          }
          text={
            selectedFilters.datePeriod ? (
              <div className="d-flex flex-column align-items-center justify-content-center">
                <span className="date-dropdown-button-type">
                  {t(
                    `home.dashboard.filters.selectedDateButton.${selectedFilters.selectedDateButton}`,
                  )}
                </span>
                <span className="date-dropdown-button-period">{`${DateFormat.dashboardPeriodDate(selectedFilters.datePeriod.beginPeriod)} - ${DateFormat.dashboardPeriodDate(selectedFilters.datePeriod.endPeriod)}`}</span>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-center">
                <span className="text-nowrap">
                  {t("home.dashboard.filters.date")}
                </span>
              </div>
            )
          }
          icon={<ChevronDownIcon />}
        />
      </Dropdown>
    </div>
  );
};

export default DateFilter;
